import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plane, Hotel, Compass, Search, Plus, Tag, ArrowLeft, MapPin, Calendar, Users, MessageCircle, Heart, Eye, Send, Bell, Star, Clock } from "lucide-react";
import { toast } from "sonner";
import { safeRedirect } from "@/utils/api";

const API = process.env.REACT_APP_BACKEND_URL;

const SEARCH_HISTORY_KEY = "savecation_mkt_search_history";
function getSearchHistory() {
  try { return JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || "[]"); } catch { return []; }
}
function saveSearchHistory(term) {
  if (!term.trim()) return;
  try {
    const history = [term, ...getSearchHistory().filter(h => h !== term)].slice(0, 5);
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history));
  } catch {}
}

export default function Marketplace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState("browse"); // browse, create, detail, my-listings, messages
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({ listing_type: "", location: "", sort_by: "newest", min_price: "", max_price: "", min_discount: "" });
  const [user, setUser] = useState(null);
  const [searchHistory, setSearchHistory] = useState(getSearchHistory);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(setUser)
      .catch(() => {});
  }, []);

  const fetchListings = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.listing_type) params.set("listing_type", filters.listing_type);
      if (filters.location) params.set("location", filters.location);
      if (filters.min_price) params.set("min_price", filters.min_price);
      if (filters.max_price) params.set("max_price", filters.max_price);
      if (filters.min_discount) params.set("min_discount", filters.min_discount);
      params.set("sort_by", filters.sort_by);
      const res = await fetch(`${API}/api/marketplace/listings?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load listings");
      const data = await res.json();
      setListings(data.listings || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load marketplace listings.");
    }
    setIsLoading(false);
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/marketplace/stats`, { credentials: "include" });
      if (!res.ok) return;
      setStats(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchListings(); fetchStats(); }, [fetchListings, fetchStats]);

  useEffect(() => {
    const purchased = searchParams.get("purchased");
    if (purchased) {
      toast.success("Purchase complete! Check messages for transfer details.");
      fetchListings();
    }
  }, [searchParams, fetchListings]);

  if (view === "create") return <CreateListing user={user} onBack={() => { setView("browse"); fetchListings(); }} />;
  if (view === "detail" && selectedListing) return <ListingDetail listing={selectedListing} user={user} onBack={() => { setView("browse"); fetchListings(); }} />;
  if (view === "my-listings") return <MyListings user={user} onBack={() => setView("browse")} />;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="marketplace-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Transfer Market</h1>
              <p className="text-sm text-slate-500">Buy & sell non-refundable bookings</p>
            </div>
            <div className="flex gap-2">
              {user && (
                <button
                  onClick={() => setView("my-listings")}
                  className="px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-full hover:bg-slate-200"
                  data-testid="my-listings-btn"
                >
                  My Listings
                </button>
              )}
              <button
                onClick={() => user ? setView("create") : navigate("/auth")}
                className="px-4 py-2 text-sm font-medium text-white bg-sky-500 rounded-full hover:bg-sky-600 flex items-center gap-1"
                data-testid="create-listing-btn"
              >
                <Plus className="w-4 h-4" /> {user ? "Sell" : "Sign in to Sell"}
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          {stats && (
            <div className="flex gap-4 py-2 text-center">
              <div className="flex-1 bg-sky-50 rounded-xl p-2">
                <div className="text-lg font-bold text-sky-600">{stats.active_listings}</div>
                <div className="text-[10px] text-slate-500">Active</div>
              </div>
              <div className="flex-1 bg-emerald-50 rounded-xl p-2">
                <div className="text-lg font-bold text-emerald-600">{stats.completed_transfers}</div>
                <div className="text-[10px] text-slate-500">Transferred</div>
              </div>
              <div className="flex-1 bg-amber-50 rounded-xl p-2">
                <div className="text-lg font-bold text-amber-600">${stats.total_saved_by_buyers?.toFixed(0) || 0}</div>
                <div className="text-[10px] text-slate-500">Saved</div>
              </div>
              <div className="flex-1 bg-purple-50 rounded-xl p-2">
                <div className="text-lg font-bold text-purple-600">{stats.avg_discount || 0}%</div>
                <div className="text-[10px] text-slate-500">Avg Off</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="mt-3 space-y-2">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search location..."
                  className="w-full pl-9 pr-3 py-2 rounded-full bg-slate-100 text-sm border-0 focus:ring-2 focus:ring-sky-300"
                  value={filters.location}
                  onFocus={() => setShowHistory(true)}
                  onBlur={() => setTimeout(() => setShowHistory(false), 150)}
                  onChange={(e) => setFilters(f => ({ ...f, location: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && filters.location.trim()) {
                      saveSearchHistory(filters.location.trim());
                      setSearchHistory(getSearchHistory());
                    }
                  }}
                  data-testid="marketplace-search"
                />
                {showHistory && searchHistory.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-2xl shadow-lg border border-slate-100 z-10 overflow-hidden">
                    {searchHistory.map((h, i) => (
                      <button key={i} className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-600"
                        onMouseDown={() => setFilters(f => ({ ...f, location: h }))}>
                        <Clock className="w-3 h-3 text-slate-400" /> {h}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {["", "hotel", "flight", "activity"].map(type => (
                <button
                  key={type || "all"}
                  onClick={() => setFilters(f => ({ ...f, listing_type: type }))}
                  className={`px-3 py-2 rounded-full text-xs font-medium transition-colors ${
                    filters.listing_type === type ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                  data-testid={`filter-${type || "all"}`}
                >
                  {type === "" ? "All" : type === "hotel" ? "Hotels" : type === "flight" ? "Flights" : "Activities"}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="number" placeholder="Min $" value={filters.min_price}
                onChange={e => setFilters(f => ({ ...f, min_price: e.target.value }))}
                className="flex-1 px-3 py-1.5 rounded-xl bg-slate-100 text-xs border-0 focus:ring-2 focus:ring-sky-300" />
              <input type="number" placeholder="Max $" value={filters.max_price}
                onChange={e => setFilters(f => ({ ...f, max_price: e.target.value }))}
                className="flex-1 px-3 py-1.5 rounded-xl bg-slate-100 text-xs border-0 focus:ring-2 focus:ring-sky-300" />
              <select value={filters.min_discount} onChange={e => setFilters(f => ({ ...f, min_discount: e.target.value }))}
                className="flex-1 px-3 py-1.5 rounded-xl bg-slate-100 text-xs border-0">
                <option value="">Any % off</option>
                <option value="10">10%+ off</option>
                <option value="20">20%+ off</option>
                <option value="30">30%+ off</option>
                <option value="50">50%+ off</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Listings */}
      <div className="max-w-2xl mx-auto px-4 py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16">
            <Tag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">No listings found</h3>
            <p className="text-sm text-slate-500 mt-1">Be the first to list a booking!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {listings.map(listing => (
              <ListingCard
                key={listing.listing_id}
                listing={listing}
                onClick={() => { setSelectedListing(listing); setView("detail"); }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ListingCard({ listing, onClick }) {
  const typeIcons = { hotel: Hotel, flight: Plane, activity: Compass };
  const TypeIcon = typeIcons[listing.listing_type] || Tag;
  const typeColors = { hotel: "text-blue-500 bg-blue-50", flight: "text-sky-500 bg-sky-50", activity: "text-emerald-500 bg-emerald-50" };

  const daysUntilExpiry = listing.expires_at
    ? Math.max(0, Math.ceil((new Date(listing.expires_at) - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 7;

  return (
    <button
      onClick={onClick}
      className="w-full bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow text-left"
      data-testid={`listing-${listing.listing_id}`}
    >
      <div className="flex">
        <div className="w-28 aspect-square flex-shrink-0 relative overflow-hidden">
          <img src={listing.image_url} alt={listing.title} className="w-full h-full object-cover" />
          {isExpiringSoon && (
            <div className="absolute bottom-0 left-0 right-0 bg-red-500/90 text-white text-[9px] font-bold text-center py-0.5">
              Expires in {daysUntilExpiry}d
            </div>
          )}
        </div>
        <div className="flex-1 p-3">
          <div className="flex items-start justify-between mb-1">
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${typeColors[listing.listing_type]}`}>
              <TypeIcon className="w-3 h-3" />
              {listing.listing_type}
            </div>
            <div className="bg-red-50 text-red-600 px-2 py-0.5 rounded-full text-[10px] font-bold">
              {listing.discount_pct}% OFF
            </div>
          </div>
          <h3 className="font-semibold text-slate-900 text-sm leading-tight line-clamp-1">{listing.title}</h3>
          {listing.location && (
            <p className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" /> {listing.location}
            </p>
          )}
          <div className="flex items-end gap-2 mt-1">
            <span className="text-base font-bold text-sky-600">${listing.asking_price}</span>
            <span className="text-xs text-slate-400 line-through">${listing.original_price}</span>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
              <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" /> {listing.views}</span>
              <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" /> {listing.saves}</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-slate-500" data-testid={`seller-${listing.listing_id}`}>
              <div className="w-4 h-4 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center text-[8px] font-bold">
                {(listing.seller_name || "?")[0].toUpperCase()}
              </div>
              <span className="truncate max-w-[60px]">{listing.seller_name || "Anonymous"}</span>
              {listing.seller_avg_rating && (
                <span className="flex items-center gap-0.5 text-amber-500 font-bold">
                  <Star className="w-3 h-3 fill-amber-400" /> {listing.seller_avg_rating.toFixed(1)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function ListingDetail({ listing, user, onBack }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showMessages, setShowMessages] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [alertPrice, setAlertPrice] = useState("");
  const [alertSet, setAlertSet] = useState(false);

  useEffect(() => {
    if (user && showMessages) {
      fetch(`${API}/api/marketplace/listings/${listing.listing_id}/messages`, { credentials: "include" })
        .then(r => r.json()).then(setMessages).catch(() => {});
    }
  }, [listing.listing_id, user, showMessages]);

  const handlePurchase = async (useWallet) => {
    if (!user) { toast.error("Sign in to purchase"); return; }
    setIsPurchasing(true);
    try {
      const res = await fetch(`${API}/api/marketplace/listings/${listing.listing_id}/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ use_wallet: useWallet, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      if (data.url) { safeRedirect(data.url); }
      else { toast.success(data.message); onBack(); }
    } catch (e) { toast.error(e.message); }
    setIsPurchasing(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      const res = await fetch(`${API}/api/marketplace/listings/${listing.listing_id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: newMessage }),
      });
      const msg = await res.json();
      setMessages(prev => [...prev, msg]);
      setNewMessage("");
    } catch (e) { toast.error("Failed to send message"); }
  };

  const typeLabels = { hotel: "Hotel Transfer", flight: "Flight Transfer", activity: "Activity Transfer" };
  const isOwner = user?.user_id === listing.seller_id;
  const canBuy = user && !isOwner && listing.status === "active";

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="listing-detail">
      <div className="bg-white">
        <div className="relative">
          <img src={listing.image_url} alt={listing.title} className="w-full h-48 object-cover" />
          <button onClick={onBack} className="absolute top-4 left-4 bg-white/90 p-2 rounded-full shadow"><ArrowLeft className="w-5 h-5" /></button>
          <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
            {listing.discount_pct}% OFF
          </div>
        </div>
        <div className="px-4 py-4">
          <div className="text-xs font-medium text-sky-500 mb-1">{typeLabels[listing.listing_type]}</div>
          <h1 className="text-xl font-bold text-slate-900">{listing.title}</h1>
          {listing.location && (
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" /> {listing.location}</p>
          )}
          {listing.description && <p className="text-sm text-slate-600 mt-3">{listing.description}</p>}

          {/* Pricing */}
          <div className="bg-slate-50 rounded-xl p-4 mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">Original Price</span>
              <span className="text-sm text-slate-400 line-through">${listing.original_price}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700">Transfer Price</span>
              <span className="text-xl font-bold text-sky-600">${listing.asking_price}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-emerald-600 font-medium">You Save</span>
              <span className="text-sm font-bold text-emerald-600">${(listing.original_price - listing.asking_price).toFixed(2)}</span>
            </div>
          </div>

          {/* Details */}
          <div className="mt-4 space-y-2">
            {listing.hotel_name && <DetailRow icon={Hotel} label="Hotel" value={listing.hotel_name} />}
            {listing.room_type && <DetailRow icon={Hotel} label="Room" value={listing.room_type} />}
            {listing.airline && <DetailRow icon={Plane} label="Airline" value={listing.airline} />}
            {listing.origin && listing.destination && <DetailRow icon={Plane} label="Route" value={`${listing.origin} → ${listing.destination}`} />}
            {listing.check_in_date && <DetailRow icon={Calendar} label="Check-in" value={listing.check_in_date} />}
            {listing.check_out_date && <DetailRow icon={Calendar} label="Check-out" value={listing.check_out_date} />}
            {listing.departure_date && <DetailRow icon={Calendar} label="Departure" value={listing.departure_date} />}
            {listing.return_date && <DetailRow icon={Calendar} label="Return" value={listing.return_date} />}
            {listing.passengers > 1 && <DetailRow icon={Users} label="Passengers" value={listing.passengers} />}
            {listing.nights > 1 && <DetailRow icon={Calendar} label="Nights" value={listing.nights} />}
            {listing.provider_name && <DetailRow icon={Tag} label="Provider" value={listing.provider_name} />}
          </div>

          {/* Transfer Info */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-4">
            <p className="text-xs font-medium text-amber-800">Transfer Method: {listing.transfer_method === "name_change" ? "Name Change" : listing.transfer_method === "booking_transfer" ? "Booking Transfer" : "Contact Provider"}</p>
            {listing.transfer_notes && <p className="text-xs text-amber-700 mt-1">{listing.transfer_notes}</p>}
          </div>

          {/* Seller */}
          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100">
            <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 font-bold">
              {(listing.seller_name || "?")[0].toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{listing.seller_name}</p>
              <p className="text-xs text-slate-400">Listed {new Date(listing.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Actions */}
          {!user && listing.status === "active" && (
            <div className="mt-4 space-y-2" data-testid="signin-prompt">
              <button
                onClick={() => window.location.href = "/auth"}
                className="w-full py-3 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-600"
                data-testid="signin-to-buy-btn"
              >
                Sign In to Purchase
              </button>
              <p className="text-center text-xs text-slate-400">Sign in to buy, message the seller, or set price alerts</p>
            </div>
          )}

          {canBuy && (
            <div className="mt-4 space-y-2">
              {user?.wallet_balance >= listing.asking_price && (
                <button
                  onClick={() => handlePurchase(true)}
                  disabled={isPurchasing}
                  className="w-full py-3 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-600 disabled:opacity-50"
                  data-testid="buy-wallet-btn"
                >
                  {isPurchasing ? "Processing..." : `Buy with Wallet ($${user.wallet_balance.toFixed(2)} balance)`}
                </button>
              )}
              <button
                onClick={() => handlePurchase(false)}
                disabled={isPurchasing}
                className="w-full py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 disabled:opacity-50"
                data-testid="buy-stripe-btn"
              >
                {isPurchasing ? "Processing..." : `Pay $${listing.asking_price} with Card`}
              </button>
              <p className="text-center text-[11px] text-slate-400">8% platform fee included. Seller receives ${listing.seller_payout?.toFixed(2)}</p>
            </div>
          )}

          {listing.status === "sold" && (
            <div className="mt-4 bg-emerald-50 text-emerald-700 text-center py-3 rounded-xl font-medium">This listing has been sold</div>
          )}

          {/* Price Drop Alert */}
          {user && !isOwner && listing.status === "active" && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3">
              {alertSet ? (
                <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                  <Bell className="w-4 h-4" /> Alert set for ${alertPrice}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-amber-600 shrink-0" />
                  <input
                    type="number"
                    value={alertPrice}
                    onChange={e => setAlertPrice(e.target.value)}
                    placeholder={`Alert me below $${listing.asking_price}`}
                    className="flex-1 px-3 py-1.5 rounded-lg bg-white border border-amber-200 text-sm"
                    data-testid="price-alert-input"
                  />
                  <button
                    onClick={async () => {
                      if (!alertPrice || parseFloat(alertPrice) >= listing.asking_price) {
                        toast.error("Target must be below current price");
                        return;
                      }
                      try {
                        await fetch(`${API}/api/marketplace/alerts/`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({ listing_id: listing.listing_id, target_price: parseFloat(alertPrice) }),
                        });
                        setAlertSet(true);
                        toast.success("Price drop alert set!");
                      } catch { toast.error("Failed to set alert"); }
                    }}
                    className="px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600"
                    data-testid="set-alert-btn"
                  >
                    Set Alert
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Messages */}
          {user && (
            <div className="mt-4">
              <button
                onClick={() => setShowMessages(!showMessages)}
                className="flex items-center gap-2 text-sm text-sky-600 font-medium"
                data-testid="toggle-messages"
              >
                <MessageCircle className="w-4 h-4" /> {showMessages ? "Hide" : "Show"} Messages
              </button>
              {showMessages && (
                <div className="mt-3 bg-slate-50 rounded-xl p-3 max-h-60 overflow-y-auto">
                  {messages.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4">No messages yet. Ask a question!</p>
                  ) : (
                    messages.map(msg => (
                      <div key={msg.message_id} className={`mb-2 ${msg.sender_id === user.user_id ? "text-right" : ""}`}>
                        <span className="text-[10px] text-slate-400">{msg.sender_name}</span>
                        <div className={`inline-block px-3 py-1.5 rounded-xl text-sm ${msg.sender_id === user.user_id ? "bg-sky-500 text-white" : "bg-white text-slate-700"}`}>
                          {msg.content}
                        </div>
                      </div>
                    ))
                  )}
                  <div className="flex gap-2 mt-2">
                    <input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                      placeholder="Type a message..."
                      className="flex-1 px-3 py-2 rounded-full bg-white border border-slate-200 text-sm"
                    />
                    <button onClick={handleSendMessage} className="p-2 bg-sky-500 text-white rounded-full"><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4 text-slate-400" />
      <span className="text-slate-500">{label}:</span>
      <span className="font-medium text-slate-700">{value}</span>
    </div>
  );
}

function CreateListing({ onBack }) {
  const [form, setForm] = useState({
    listing_type: "hotel", title: "", description: "", original_price: "", asking_price: "",
    provider_name: "", location: "", hotel_name: "", room_type: "", nights: 1,
    check_in_date: "", check_out_date: "", airline: "", origin: "", destination: "",
    departure_date: "", return_date: "", passengers: 1,
    transfer_method: "name_change", transfer_notes: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.original_price || !form.asking_price) {
      toast.error("Fill in title, original price, and asking price");
      return;
    }
    if (parseFloat(form.asking_price) >= parseFloat(form.original_price)) {
      toast.error("Asking price must be less than original price");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = { ...form, original_price: parseFloat(form.original_price), asking_price: parseFloat(form.asking_price), nights: parseInt(form.nights), passengers: parseInt(form.passengers) };
      const res = await fetch(`${API}/api/marketplace/listings`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success("Listing created!");
      onBack();
    } catch (e) { toast.error(e.message); }
    setIsSubmitting(false);
  };

  const discount = form.original_price && form.asking_price
    ? Math.round(((parseFloat(form.original_price) - parseFloat(form.asking_price)) / parseFloat(form.original_price)) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="create-listing-page">
      <div className="bg-white px-4 py-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-1"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
          <h1 className="text-lg font-bold text-slate-900">Sell Your Booking</h1>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Type */}
        <div className="flex gap-2">
          {[{ v: "hotel", l: "Hotel", i: Hotel }, { v: "flight", l: "Flight", i: Plane }, { v: "activity", l: "Activity", i: Compass }].map(({ v, l, i: I }) => (
            <button key={v} type="button"
              onClick={() => setForm(f => ({ ...f, listing_type: v }))}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-colors ${form.listing_type === v ? "bg-sky-500 text-white" : "bg-white text-slate-600 border border-slate-200"}`}
            >
              <I className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>

        <Input label="Title" value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} placeholder="e.g. Marriott Paris - 3 nights" required />
        <Input label="Description" value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} placeholder="Describe your booking..." textarea />

        <div className="grid grid-cols-2 gap-3">
          <Input label="Original Price ($)" type="number" value={form.original_price} onChange={v => setForm(f => ({ ...f, original_price: v }))} placeholder="500" required />
          <Input label="Asking Price ($)" type="number" value={form.asking_price} onChange={v => setForm(f => ({ ...f, asking_price: v }))} placeholder="350" required />
        </div>

        {discount > 0 && (
          <div className="bg-emerald-50 text-emerald-700 rounded-xl px-4 py-2 text-sm font-medium text-center">
            Buyers save {discount}% (${(parseFloat(form.original_price) - parseFloat(form.asking_price)).toFixed(2)} off)
          </div>
        )}

        <Input label="Provider" value={form.provider_name} onChange={v => setForm(f => ({ ...f, provider_name: v }))} placeholder="Marriott, Delta, etc." />
        <Input label="Location" value={form.location} onChange={v => setForm(f => ({ ...f, location: v }))} placeholder="Paris, France" />

        {form.listing_type === "hotel" && (
          <>
            <Input label="Hotel Name" value={form.hotel_name} onChange={v => setForm(f => ({ ...f, hotel_name: v }))} placeholder="Hotel name" />
            <Input label="Room Type" value={form.room_type} onChange={v => setForm(f => ({ ...f, room_type: v }))} placeholder="Deluxe King" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Check-in" type="date" value={form.check_in_date} onChange={v => setForm(f => ({ ...f, check_in_date: v }))} />
              <Input label="Check-out" type="date" value={form.check_out_date} onChange={v => setForm(f => ({ ...f, check_out_date: v }))} />
            </div>
          </>
        )}

        {form.listing_type === "flight" && (
          <>
            <Input label="Airline" value={form.airline} onChange={v => setForm(f => ({ ...f, airline: v }))} placeholder="Delta, United, etc." />
            <div className="grid grid-cols-2 gap-3">
              <Input label="From" value={form.origin} onChange={v => setForm(f => ({ ...f, origin: v }))} placeholder="NYC" />
              <Input label="To" value={form.destination} onChange={v => setForm(f => ({ ...f, destination: v }))} placeholder="Paris" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Departure" type="date" value={form.departure_date} onChange={v => setForm(f => ({ ...f, departure_date: v }))} />
              <Input label="Return" type="date" value={form.return_date} onChange={v => setForm(f => ({ ...f, return_date: v }))} />
            </div>
          </>
        )}

        {/* Transfer Method */}
        <div>
          <label className="text-sm font-medium text-slate-700 mb-1 block">Transfer Method</label>
          <select
            value={form.transfer_method}
            onChange={(e) => setForm(f => ({ ...f, transfer_method: e.target.value }))}
            className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm"
          >
            <option value="name_change">Name Change with Provider</option>
            <option value="booking_transfer">Direct Booking Transfer</option>
            <option value="contact_provider">Buyer Contacts Provider</option>
          </select>
        </div>

        <Input label="Transfer Notes" value={form.transfer_notes} onChange={v => setForm(f => ({ ...f, transfer_notes: v }))} placeholder="How the transfer will work..." textarea />

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 bg-sky-500 text-white rounded-xl font-semibold hover:bg-sky-600 disabled:opacity-50"
          data-testid="submit-listing-btn"
        >
          {isSubmitting ? "Creating..." : "List for Sale"}
        </button>
        <p className="text-center text-xs text-slate-400">8% commission on successful sale. Free tier: 2 active listings max.</p>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, required, textarea }) {
  const cls = "w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:ring-2 focus:ring-sky-300 focus:border-sky-300";
  return (
    <div>
      {label && <label className="text-sm font-medium text-slate-700 mb-1 block">{label}</label>}
      {textarea ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} className={cls} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} required={required} className={cls} />
      )}
    </div>
  );
}

function MyListings({ onBack }) {
  const [listings, setListings] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [tab, setTab] = useState("selling");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/marketplace/my-listings`, { credentials: "include" }).then(r => r.json()),
      fetch(`${API}/api/marketplace/my-purchases`, { credentials: "include" }).then(r => r.json()),
    ]).then(([l, p]) => { setListings(l || []); setPurchases(p || []); setIsLoading(false); }).catch(() => setIsLoading(false));
  }, []);

  const handleCancel = async (listingId) => {
    if (!window.confirm("Cancel this listing? This cannot be undone.")) return;
    try {
      await fetch(`${API}/api/marketplace/listings/${listingId}`, { method: "DELETE", credentials: "include" });
      setListings(prev => prev.map(l => l.listing_id === listingId ? { ...l, status: "cancelled" } : l));
      toast.success("Listing cancelled");
    } catch (e) { toast.error("Failed to cancel"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="bg-white px-4 py-4 border-b border-slate-200">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={onBack} className="p-1"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
          <h1 className="text-lg font-bold text-slate-900">My Marketplace</h1>
        </div>
        <div className="flex gap-2">
          {["selling", "purchases"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-2 rounded-full text-sm font-medium ${tab === t ? "bg-sky-500 text-white" : "bg-slate-100 text-slate-600"}`}
            >
              {t === "selling" ? `Selling (${listings.length})` : `Purchased (${purchases.length})`}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-sky-200 border-t-sky-500 rounded-full animate-spin" /></div>
        ) : (tab === "selling" ? listings : purchases).length === 0 ? (
          <p className="text-center text-slate-400 py-12">Nothing here yet</p>
        ) : (
          (tab === "selling" ? listings : purchases).map(l => (
            <div key={l.listing_id} className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-900 text-sm">{l.title}</h3>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  l.status === "active" ? "bg-emerald-50 text-emerald-600" :
                  l.status === "sold" ? "bg-sky-50 text-sky-600" : "bg-slate-100 text-slate-400"
                }`}>{l.status}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="font-bold text-sky-600">${l.asking_price}</span>
                <span className="text-slate-400 line-through text-xs">${l.original_price}</span>
                {l.views > 0 && <span className="text-slate-400 text-xs flex items-center gap-1"><Eye className="w-3 h-3" /> {l.views}</span>}
              </div>
              {tab === "selling" && l.status === "active" && (
                <button onClick={() => handleCancel(l.listing_id)} className="mt-2 text-xs text-red-500 hover:text-red-600 font-medium">Cancel Listing</button>
              )}
              {l.status === "sold" && tab === "selling" && (
                <p className="mt-2 text-xs text-emerald-600 font-medium">Sold! ${l.seller_payout?.toFixed(2)} credited to your wallet</p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
